import "./index.css";
import { useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Login } from "./routes/Login";
import { Signup } from "./routes/Signup";
import { ForgotPassword } from "./routes/ForgotPassword";
import { ResetPassword } from "./routes/ResetPassword";
import { OnboardingInstagramRoute } from "./routes/onboarding/OnboardingInstagramRoute";
import { OnboardingRoute } from "./routes/onboarding/OnboardingRoute";
import { InstagramFansRoute } from "./routes/instagram/InstagramFansRoute";
import { InstagramFansListRoute } from "./routes/instagram/InstagramFansListRoute";
import { Dashboard } from "./routes/Dashboard";
import { TourList } from "./routes/tours/TourList";
import { TourView } from "./routes/tours/TourView";
import { EventEdit } from "./routes/tours/EventEdit";
import { Events } from "./routes/Events";
import { Tickets } from "./routes/Tickets";
import { TicketEdit } from "./routes/TicketEdit";
import { Orders } from "./routes/Orders";
import { Checkouts } from "./routes/Checkouts";
import { OrderEdit } from "./routes/OrderEdit";
import { CollectPaymentRoute } from "./routes/sales/CollectPaymentRoute";
import { OrderShipmentEmailPreview } from "./routes/OrderShipmentEmailPreview";
import { CreateOrder } from "./routes/CreateOrder";
import { Presales } from "./routes/Presales";
import { Products } from "./routes/Products";
import { Discounts } from "./routes/Discounts";
import { ProductEdit } from "./routes/ProductEdit";
import { Contacts } from "./routes/Contacts";
import { ContactsSearch } from "./routes/ContactsSearch";
import { ContactsInstagramMerge } from "./routes/ContactsInstagramMerge";
import { ContactsEmailMerge } from "./routes/ContactsEmailMerge";
import { ContactsPhoneNumberMerge } from "./routes/ContactsPhoneNumberMerge";
import { ContactsImport } from "./routes/ContactsImport";
import { ContactLayout } from "./routes/ContactLayout";
import { ContactEdit } from "./routes/ContactEdit";
import { ContactActivity } from "./routes/ContactActivity";
import { EmailCampaigns } from "./routes/EmailCampaigns";
import { EmailCampaignCompose } from "./routes/EmailCampaignCompose/EmailCampaignCompose";
import { EmailCampaignReportLayout } from "./routes/EmailCampaignReport";
import { EmailCampaignReportReportTab } from "./routes/EmailCampaignReport/ReportTab";
import { EmailCampaignReportPreviewTab } from "./routes/EmailCampaignReport/PreviewTab";
import { EmailCampaignReportCodeTab } from "./routes/EmailCampaignReport/CodeTab";
import { MessagesSMS, MessagesSMSSettings } from "./routes/MessagesSMS";
import { MessagesSMSCompose } from "./routes/MessagesSMSCompose";
import { MessagesSMSCampaignMessages } from "./routes/MessagesSMSCampaignMessages";
import { SMSInbox } from "./routes/SMSInbox";
import { Domains } from "./routes/Domains";
import { Files } from "./routes/Files";
import { Pages } from "./routes/Pages";
import { PageEdit } from "./routes/PageEdit";
import { SupportChats } from "./routes/SupportChats";
import { Admin } from "./routes/admin/Admin";
import { Artists } from "./routes/admin/Artists";
import { CreateArtist } from "./routes/admin/CreateArtist";
import { ArtistEditRoute } from "./routes/admin/ArtistEditRoute";
import { Users } from "./routes/admin/Users";
import { UserDetail } from "./routes/admin/UserDetail";
import { Automations } from "./routes/Automations";
import { AutomationsLayout } from "./routes/AutomationsLayout";
import { AutomationRuns, AutomationRunView } from "./routes/AutomationRuns";
import { AutomationEdit } from "./routes/AutomationEdit";
import { Instagram } from "./routes/automations/Instagram";
import { InstagramEdit } from "./routes/automations/InstagramEdit";
import { InstagramRuns } from "./routes/automations/InstagramRuns";
import { Settings } from "./routes/settings/Settings";
import { SettingsIntegrationsRoute } from "./routes/settings/SettingsIntegrationsRoute";
import { SettingsMetaRoute } from "./routes/settings/SettingsMetaRoute";
import { SettingsMetaBusinessRoute } from "./routes/settings/SettingsMetaBusinessRoute";
import { SettingsMetaBusinessAdAccountsRoute } from "./routes/settings/SettingsMetaBusinessAdAccountsRoute";
import { SettingsMetaBusinessAdAccountRoute } from "./routes/settings/SettingsMetaBusinessAdAccountRoute";
import { SettingsMetaBusinessAdAccountAudiencesRoute } from "./routes/settings/SettingsMetaBusinessAdAccountAudiencesRoute";
import { SettingsInstagramRoute } from "./routes/settings/SettingsInstagramRoute";
import { SettingsStripeRoute } from "./routes/settings/SettingsStripeRoute";
import { SettingsShopifyRoute } from "./routes/settings/SettingsShopifyRoute";
import { SettingsKunakiRoute } from "./routes/settings/SettingsKunakiRoute";
import { SettingsEasyShipRoute } from "./routes/settings/SettingsEasyShipRoute";
import { SettingsMailerLiteRoute } from "./routes/settings/SettingsMailerLiteRoute";
import { SettingsBrevoRoute } from "./routes/settings/SettingsBrevoRoute";
import { SettingsLayloRoute } from "./routes/settings/SettingsLayloRoute";
import { SettingsBeehiivRoute } from "./routes/settings/SettingsBeehiivRoute";
import { SettingsManychatRoute } from "./routes/settings/SettingsManychatRoute";
import { SettingsApiKeysRoute } from "./routes/settings/SettingsApiKeysRoute";
import { SettingsCheckoutRoute } from "./routes/settings/SettingsCheckoutRoute";
import { SettingsWebsiteDesignRoute } from "./routes/settings/SettingsWebsiteDesignRoute";
import { SettingsEmailRoute } from "./routes/settings/SettingsEmailRoute";
import { SettingsAccountRoute } from "./routes/settings/SettingsAccountRoute";
import { SettingsAiRoute } from "./routes/settings/SettingsAiRoute";
import { SettingsBillingRoute } from "./routes/settings/SettingsBillingRoute";
import { SettingsBillingRechargeRoute } from "./routes/settings/SettingsBillingRechargeRoute";
import { KunakiDebug } from "./routes/debug/KunakiDebug";
import { Privacy } from "./routes/Privacy";
import { Terms } from "./routes/Terms";
import { MetaDataDeletion } from "./routes/MetaDataDeletion";
import { SidebarInset, SidebarProvider } from "./components/ui/sidebar";
import { AppSidebar } from "./components/app-sidebar";
import { SiteHeader } from "./components/site-header";
import { BreadcrumbProvider } from "./contexts/BreadcrumbContext";
import { AppProvider } from "./contexts/AppContext";
import { DashboardProvider } from "./contexts/SyncContext";
import { Toaster } from "./components/ui/sonner";

function RedirectToResponsesTab() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/pages/${id}/edit?tab=responses`} replace />;
}

function RequireAuth() {
  const [status, setStatus] = useState<"loading" | "authed" | "unauthed">("loading");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch("/api/session");
        if (cancelled) return;
        setStatus(response.ok ? "authed" : "unauthed");
      } catch {
        if (!cancelled) setStatus("unauthed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") return null;
  if (status === "unauthed") return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RequireIGAccount() {
  const [status, setStatus] = useState<"loading" | "connected" | "disconnected">("loading");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch("/api/ig-account");
        if (cancelled) return;
        if (!response.ok) {
          setStatus("disconnected");
          return;
        }
        const body = (await response.json()) as { igAccount?: unknown };
        setStatus(body.igAccount ? "connected" : "disconnected");
      } catch {
        if (!cancelled) setStatus("disconnected");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") return null;
  if (status === "disconnected") return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

function RequireSubscription() {
  const [status, setStatus] = useState<"loading" | "subscribed" | "unsubscribed">("loading");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch("/api/session");
        if (cancelled) return;
        if (!response.ok) {
          setStatus("unsubscribed");
          return;
        }
        const body = (await response.json()) as { hasActiveSubscription?: boolean };
        setStatus(body.hasActiveSubscription ? "subscribed" : "unsubscribed");
      } catch {
        if (!cancelled) setStatus("unsubscribed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") return null;
  if (status === "unsubscribed") return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <AppProvider>
      <SidebarProvider
        style={
          {
            "--sidebar-width": "calc(var(--spacing) * 72)",
            "--header-height": "calc(var(--spacing) * 12)",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader />
          {children}
        </SidebarInset>
      </SidebarProvider>
    </AppProvider>
  );
}

export function App() {
  return (
    <BreadcrumbProvider>
      <Toaster />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/meta/data-deletion" element={<MetaDataDeletion />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route element={<RequireAuth />}>
        <Route path="/onboarding" element={<OnboardingRoute />} />
        <Route path="/onboarding/instagram" element={<OnboardingInstagramRoute />} />
        <Route element={<RequireIGAccount />}>
        <Route path="/instagram/fans" element={<InstagramFansRoute />} />
        <Route path="/instagram/fans/list" element={<InstagramFansListRoute />} />
        </Route>
        <Route element={<RequireSubscription />}>
        <Route path="/emails/:id/edit" element={<EmailCampaignCompose />} />
        <Route
          path="/*"
          element={
            <AppLayout>
              <Routes>
                <Route
                  path="/"
                  element={
                    <DashboardProvider>
                      <Dashboard />
                    </DashboardProvider>
                  }
                />
                <Route path="/tours" element={<TourList />} />
                <Route path="/tours/:id" element={<TourView />} />
                <Route path="/events/:eventId/edit" element={<EventEdit />} />
                <Route path="/events" element={<Events />} />
                <Route path="/tickets" element={<Tickets />} />
                <Route path="/tickets/:ticketId/edit" element={<TicketEdit />} />
                <Route path="/sales/orders/new" element={<CreateOrder />} />
                <Route path="/sales/orders/:id/collect" element={<CollectPaymentRoute />} />
                <Route path="/sales/orders/:id" element={<OrderEdit />} />
                <Route
                  path="/orders/:orderId/shipment/email/preview"
                  element={<OrderShipmentEmailPreview />}
                />
                <Route path="/sales/orders" element={<Orders />} />
                <Route path="/sales/checkouts" element={<Checkouts />} />
                <Route path="/sales" element={<Navigate to="/sales/orders" replace />} />
                <Route path="/presales" element={<Presales />} />
                <Route path="/products" element={<Products />} />
                <Route path="/discounts" element={<Discounts />} />
                <Route path="/products/:id/:tab?" element={<ProductEdit />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/search" element={<ContactsSearch />} />
                <Route
                  path="/contacts/instagram/merge"
                  element={<ContactsInstagramMerge />}
                />
                <Route
                  path="/contacts/email/merge"
                  element={<ContactsEmailMerge />}
                />
                <Route
                  path="/contacts/phone/merge"
                  element={<ContactsPhoneNumberMerge />}
                />
                <Route path="/contacts/import" element={<ContactsImport />} />
                <Route path="/contacts/:id" element={<ContactLayout />}>
                  <Route index element={<Navigate to="edit" replace />} />
                  <Route path="edit" element={<ContactEdit />} />
                  <Route path="activity" element={<ContactActivity />} />
                </Route>
                <Route path="/messages/emails" element={<EmailCampaigns />} />
                <Route path="/messages/emails/:id" element={<EmailCampaignReportLayout />}>
                  <Route path="report" element={<EmailCampaignReportReportTab />} />
                  <Route path="preview" element={<EmailCampaignReportPreviewTab />} />
                  <Route path="code" element={<EmailCampaignReportCodeTab />} />
                </Route>
                <Route path="/contacts/groups" element={<Navigate to="/contacts" replace />} />
                <Route
                  path="/contacts/group/:id/edit"
                  element={<Navigate to="/contacts" replace />}
                />
                <Route path="/messages/sms/inbox" element={<SMSInbox />} />
                <Route path="/messages/sms/inbox/:phoneNumber" element={<SMSInbox />} />
                <Route path="/messages/sms/campaigns" element={<MessagesSMS />} />
                <Route path="/messages/sms/settings" element={<MessagesSMSSettings />} />
                <Route
                  path="/messages/sms/campaigns/:id/messages"
                  element={<MessagesSMSCampaignMessages />}
                />
                <Route path="/messages/sms/campaigns/:id" element={<MessagesSMSCompose />} />
                <Route path="/messages/sms/compose" element={<MessagesSMSCompose />} />
                <Route path="/automations" element={<AutomationsLayout />}>
                  <Route index element={<Automations />} />
                  <Route path="runs" element={<AutomationRuns />} />
                  <Route path="runs/:id" element={<AutomationRunView />} />
                </Route>
                <Route path="/automations/:id/edit" element={<AutomationEdit />} />
                <Route path="/automations/instagram" element={<Instagram />} />
                <Route path="/automations/instagram/:id/edit" element={<InstagramEdit />} />
                <Route path="/automations/instagram/:id/runs" element={<InstagramRuns />} />
                <Route path="/sites/domains" element={<Domains />} />
                <Route path="/files" element={<Files />} />
                <Route path="/pages" element={<Pages />} />
                <Route path="/support" element={<SupportChats />} />
                <Route path="/support/:userUUID" element={<SupportChats />} />
                <Route path="/pages/:id/edit" element={<PageEdit />} />
                <Route
                  path="/pages/:id/responses"
                  element={<RedirectToResponsesTab />}
                />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/integrations/meta" element={<SettingsMetaRoute />} />
                <Route path="/settings/meta/businesses/:businessId" element={<SettingsMetaBusinessRoute />} />
                <Route
                  path="/settings/meta/businesses/:businessId/ad-accounts"
                  element={<SettingsMetaBusinessAdAccountsRoute />}
                />
                <Route
                  path="/settings/meta/businesses/:businessId/ad-accounts/:adAccountId"
                  element={<SettingsMetaBusinessAdAccountRoute />}
                />
                <Route
                  path="/settings/meta/businesses/:businessId/ad-accounts/:adAccountId/audiences"
                  element={<SettingsMetaBusinessAdAccountAudiencesRoute />}
                />
                <Route path="/settings/integrations/instagram" element={<SettingsInstagramRoute />} />
                <Route path="/settings/integrations/stripe" element={<SettingsStripeRoute />} />
                <Route path="/settings/integrations/shopify" element={<SettingsShopifyRoute />} />
                <Route path="/settings/integrations/kunaki" element={<SettingsKunakiRoute />} />
                <Route path="/settings/integrations/easyship" element={<SettingsEasyShipRoute />} />
                <Route path="/settings/integrations/mailerlite" element={<SettingsMailerLiteRoute />} />
                <Route path="/settings/integrations/brevo" element={<SettingsBrevoRoute />} />
                <Route path="/settings/integrations/laylo" element={<SettingsLayloRoute />} />
                <Route path="/settings/integrations/beehiiv" element={<SettingsBeehiivRoute />} />
                <Route path="/settings/integrations/manychat" element={<SettingsManychatRoute />} />
                <Route path="/settings/integrations/api-keys" element={<SettingsApiKeysRoute />} />
                <Route path="/settings/integrations" element={<SettingsIntegrationsRoute />} />
                <Route path="/settings/checkout" element={<SettingsCheckoutRoute />} />
                <Route path="/settings/website" element={<SettingsWebsiteDesignRoute />} />
                <Route path="/settings/email" element={<SettingsEmailRoute />} />
                <Route path="/settings/account" element={<SettingsAccountRoute />} />
                <Route path="/settings/billing/recharge" element={<SettingsBillingRechargeRoute />} />
                <Route path="/settings/billing" element={<SettingsBillingRoute />} />
                <Route path="/settings/phone" element={<Navigate to="/messages/sms/settings" replace />} />
                <Route path="/settings/ai" element={<SettingsAiRoute />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/artists" element={<Artists />} />
                <Route path="/admin/artists/create" element={<CreateArtist />} />
                <Route
                  path="/admin/artists/:artistId"
                  element={<Navigate to="overview" replace />}
                />
                <Route
                  path="/admin/artists/:artistId/overview"
                  element={<ArtistEditRoute />}
                />
                <Route
                  path="/admin/artists/:artistId/balance"
                  element={<ArtistEditRoute />}
                />
                <Route
                  path="/admin/artists/:artistId/subscription"
                  element={<ArtistEditRoute />}
                />
                <Route path="/admin/users" element={<Users />} />
                <Route path="/admin/users/:userId" element={<UserDetail />} />
              </Routes>
            </AppLayout>
          }
        />
        </Route>
        </Route>
      </Routes>
    </BreadcrumbProvider>
  );
}

export default App;
